import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import API from '../../../api/api'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import { getFullName, getAttendeeStatus } from '../utils'

const emptyData = {
  name: '',
  taken: 0,
  attendees: [],
}

const mapAttendees = (attendees = [], guests = [], t) => {
  const mappedAttendees = []

  attendees.forEach((attendee) => {
    mappedAttendees.push({
      id: attendee.id,
      user: {
        avatar: attendee.user.avatar,
        name: getFullName(attendee.user.firstName, attendee.user.lastName),
        apartment: attendee.apartment,
        email: attendee.user.email,
        address: attendee.street,
        phone: attendee.phone,
        role: attendee.user.role,
      },
      payer: attendee.paidData?.payerName,
      ticketType: attendee.ticketType,
      ticketPrice: attendee.ticketPrice,
      status: getAttendeeStatus(attendee.isRemoved, attendee.participation, t),
      isRemoved: attendee.isRemoved,
      isFree: !attendee.ticketPrice,
      isCheckedIn: attendee.participation,
      isRefunded: attendee.isRefunded,
      guests: attendee.guests,
      refundedAmount: attendee.isRefunded ? attendee.ticketPrice : 0,
    })
  })

  guests.forEach((guest) => {
    const name = `${getFullName(guest.firstName, guest.lastName)}${guest.isAnonymous ? '' : t('attendees:user.guestSuffix')}`
    mappedAttendees.push({
      id: guest.id,
      user: {
        avatar: '',
        name: name,
        apartment: guest.apartment,
        email: guest.email,
        address: guest.street,
        phone: guest.phone,
        role: 'Guest',
      },
      payer: guest.payerName,
      ticketType: guest.ticketType,
      ticketPrice: guest.ticketPrice,
      status: getAttendeeStatus(guest.isRemoved, guest.participation, t),
      isRemoved: guest.isRemoved,
      isFree: !guest.ticketPrice,
      isCheckedIn: guest.participation,
      isRefunded: guest.isRefunded,
      guests: [],
      refundedAmount: guest.isRefunded ? guest.ticketPrice : 0,
    })
  })

  const isGuest = (attendee) => attendee.user.role === 'Guest'

  const isPayer = (attendee) => !isGuest(attendee) && attendee.payer === attendee.user.name

  const householdKey = (attendee) =>
    isGuest(attendee) ? attendee.payer : attendee.payer || attendee.user.name

  const priority = (attendee) => {
    if (isPayer(attendee)) return 0
    if (isGuest(attendee)) return 1
    return 2 // household member
  }

  mappedAttendees.sort((a, b) => {
    // 1. Unit
    const apartmentCompare = (a.user.apartment || '').localeCompare(
      b.user.apartment || '',
      undefined,
      { sensitivity: 'base' },
    )

    if (apartmentCompare !== 0) {
      return apartmentCompare
    }

    // 2. Household group
    const householdCompare = householdKey(a).localeCompare(householdKey(b))

    if (householdCompare !== 0) {
      return householdCompare
    }

    // 3. Within household:
    // payer -> guests -> household members
    const priorityCompare = priority(a) - priority(b)

    if (priorityCompare !== 0) {
      return priorityCompare
    }

    // 4. Stable tie-breaker
    return a.user.name.localeCompare(b.user.name)
  })

  return mappedAttendees
}

export const useEventAttendees = ({ eventId, search, filter }) => {
  const { t } = useTranslation(['attendees'])
  const { displaySnackbar } = useSnackbar()
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState(emptyData)

  const fetchData = async () => {
    try {
      setIsError(false)
      setIsLoading(true)

      const result = await API.getEventAttendees({ eventId, search, filter })
      setData({
        ...result.data,
        attendees: mapAttendees(result.data.attendees, result.data.guests, t),
      })
    } catch (error) {
      console.error(error)
      displaySnackbar(t('attendees:errors.loadAttendeesList'), true)
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!eventId) {
      return
    }
    fetchData()
  }, [eventId, search, filter])

  return { fetchData, isLoading, isError, data }
}
